import { BreakpointObserver } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppStepIndicatorViewModel, getAdjacentSteps, updateSteps } from '@classifieds-sell/shared';
import { MotorsDraftFacade, IRouterData, RouterFacade } from '@classifieds-sell/state';
import { TRADEME_CORE_UI_COMPACT_MAX_WIDTH } from '@classifieds-sell/trademe-core-ui';
import { from, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StepIds } from '../../models/motors-step/motors-step-model';

@Component({
    selector: 'motors-tabs',
    templateUrl: './motors-tabs.component.html',
    styleUrls: ['./motors-tabs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MotorsTabsComponent implements OnInit {
    private _destroyed$ = new Subject<void>();
    private _routerData: IRouterData;

    public isCompact: boolean;

    @Input() selectedStepId: StepIds;
    @Output() beforeStepChange = new EventEmitter<{
        cb: (arg?: unknown) => void;
        arg?: unknown;
        isBack?: boolean;
    }>();

    public stepIndicatorViewModel: AppStepIndicatorViewModel;
    photosUploading$ = this._draftFacade.photosUploading$;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _breakpointObserver: BreakpointObserver,
        private _routerFacade: RouterFacade,
        private _draftFacade: MotorsDraftFacade,
        private _router: Router,
        private _activatedRoute: ActivatedRoute
    ) {}

    ngOnInit(): void {
        from(this._routerFacade.routerData$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((stateData) => {
                this._routerData = stateData;
                this.updateStepIndicator();
            });

        // Page validation results to determine if any steps need to be enabled
        from(this._draftFacade.validationResults$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((validationResults) => {
                if (validationResults && validationResults.length > 0) {
                    const steps = updateSteps(
                        this._routerData.steps,
                        validationResults
                    );
                    this._routerFacade.updateSteps(steps);
                }
            });

        this._breakpointObserver
            .observe([TRADEME_CORE_UI_COMPACT_MAX_WIDTH])
            .subscribe((result) => {
                this.isSmallChange(result.matches);
            });
    }

    public onBeforeStepChange(
        cb: (arg?: unknown) => void,
        arg?: unknown,
        isBack?: boolean
    ) {
        this.beforeStepChange.emit({ cb: cb, arg: arg, isBack: isBack });
    }

    public onSelectedStepChange(stepId: StepIds) {
        this._router.navigate([stepId], {
            relativeTo: this._activatedRoute,
            queryParams: this._activatedRoute.snapshot.queryParams
        });
    }

    private isSmallChange(isSmall: boolean) {
        this.isCompact = isSmall;
        this._changeDetectorRef.markForCheck();
    }

    private updateStepIndicator(): void {
        this.stepIndicatorViewModel = new AppStepIndicatorViewModel(
            [
                this._routerData.categoryId.toString(),
                this._routerData.subCategory
            ],
            this._routerData.steps,
            this._routerData.stepInfo?.stepId
        );
        this.selectedStepId = this.stepIndicatorViewModel
            ?.selectedStepId as StepIds;

        const adjacentSteps = getAdjacentSteps(
            this._routerData.steps,
            this.selectedStepId
        );
        if (
            (this._routerData.stepInfo?.stepId || '') !== this.selectedStepId ||
            (this._routerData.stepInfo?.nextStepId || '') !==
                (adjacentSteps[1] || '') ||
            (this._routerData.stepInfo?.prevStepId || '') !==
                (adjacentSteps[0] || '')
        ) {
            this.updateStepInfo(
                this.selectedStepId,
                adjacentSteps[1],
                adjacentSteps[0]
            );
        }

        this._changeDetectorRef.markForCheck();
    }

    private updateStepInfo(
        currentStepId: string,
        nextStepId: string,
        prevStepId: string
    ): void {
        const currentStep = this.stepIndicatorViewModel.steps.find(
            (s) => s.stepId === currentStepId
        );

        const nextStep = this.stepIndicatorViewModel.steps.find(
            (s) => s.stepId === nextStepId
        );

        const prevStep = this.stepIndicatorViewModel.steps.find(
            (s) => s.stepId === prevStepId
        );

        const stepInfo = {
            stepId: currentStep?.stepId,
            step: currentStep,
            nextStepId: nextStep?.stepId,
            nextStep: nextStep,
            prevStepId: prevStep?.stepId,
            prevStep: prevStep
        };

        this._routerFacade.setRouterStepInfo(stepInfo);
    }
}
